/**
 * Sanitize text input — strip HTML tags, script injections, and control chars.
 * Safe for names, addresses, and general text fields.
 */
export function sanitizeInput(value: string): string {
  return value
    .replace(/<[^>]*>/g, '')            // Strip HTML tags
    .replace(/[<>"'`]/g, '')            // Remove chars used in XSS/injection
    .replace(/javascript:/gi, '')       // Strip JS protocol
    .replace(/on\w+\s*=/gi, '')         // Strip inline event handlers
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '') // Strip control chars
    .trim()
}

/**
 * Sanitize phone input — keep only digits, spaces, dashes, parens, plus sign
 */
export function sanitizePhone(value: string): string {
  return value.replace(/[^\d\s\-()+ ]/g, '').slice(0, 20)
}

/**
 * Sanitize email input — keep only valid email chars
 */
export function sanitizeEmail(value: string): string {
  return value.replace(/[^a-zA-Z0-9@._+-]/g, '').slice(0, 254)
}

/**
 * Validate email address
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate phone number — accepts 10-digit (local) or 11-digit (with country code) formats.
 * Allows digits, spaces, dashes, parens, and leading +.
 */
export function isValidPhone(phone: string): boolean {
  const phoneRegex = /^[+\d\s\-()]+$/
  const cleaned = phone.replace(/\D/g, '')
  return phoneRegex.test(phone) && cleaned.length >= 10 && cleaned.length <= 15
}

/**
 * Validate zip code (US format)
 */
export function isValidZipCode(zipCode: string): boolean {
  const zipRegex = /^\d{5}(-\d{4})?$/
  return zipRegex.test(zipCode)
}

/**
 * Validate required field
 */
export function isRequired(value: string | null | undefined): boolean {
  if (value === null || value === undefined) return false
  return value.trim().length > 0
}

/**
 * Validate minimum length
 */
export function minLength(value: string, min: number): boolean {
  return value.length >= min
}

/**
 * Validate maximum length
 */
export function maxLength(value: string, max: number): boolean {
  return value.length <= max
}

/**
 * Get validation error message for email
 */
export function getEmailError(email: string): string | null {
  if (!isRequired(email)) return 'Email is required'
  if (!isValidEmail(email)) return 'Please enter a valid email address'
  return null
}

/**
 * Get validation error message for phone
 */
export function getPhoneError(phone: string): string | null {
  if (!isRequired(phone)) return 'Phone number is required'
  if (!isValidPhone(phone))
    return 'Please enter a valid phone number'
  return null
}

/**
 * Get validation error message for name
 */
export function getNameError(name: string, fieldName: string = 'Name'): string | null {
  if (!isRequired(name)) return `${fieldName} is required`
  if (!minLength(name, 2)) return `${fieldName} must be at least 2 characters`
  if (!maxLength(name, 50)) return `${fieldName} must be less than 50 characters`
  return null
}

/**
 * Get validation error message for zip code
 */
export function getZipCodeError(zipCode: string): string | null {
  if (!isRequired(zipCode)) return 'Zip code is required'
  if (!isValidZipCode(zipCode)) return 'Please enter a valid zip code'
  return null
}

/**
 * Validation rules for react-hook-form
 */
export const validationRules = {
  email: {
    required: 'Email is required',
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Please enter a valid email address',
    },
  },
  phone: {
    required: 'Phone number is required',
    validate: (value: string) => {
      const cleaned = value.replace(/\D/g, '')
      return cleaned.length === 10 || 'Please enter a valid 10-digit phone number'
    },
  },
  firstName: {
    required: 'First name is required',
    minLength: {
      value: 2,
      message: 'First name must be at least 2 characters',
    },
    maxLength: {
      value: 50,
      message: 'First name must be less than 50 characters',
    },
  },
  lastName: {
    required: 'Last name is required',
    minLength: {
      value: 2,
      message: 'Last name must be at least 2 characters',
    },
    maxLength: {
      value: 50,
      message: 'Last name must be less than 50 characters',
    },
  },
  street: {
    required: 'Street address is required',
    minLength: {
      value: 5,
      message: 'Street address must be at least 5 characters',
    },
  },
  city: {
    required: 'City is required',
    minLength: {
      value: 2,
      message: 'City must be at least 2 characters',
    },
  },
  state: {
    required: 'State is required',
  },
  zipCode: {
    required: 'Zip code is required',
    pattern: {
      value: /^\d{5}(-\d{4})?$/,
      message: 'Please enter a valid zip code',
    },
  },
}
